import { GetManyReferenceParams, GetManyReferenceResult, GetOneParams, GetOneResult, Identifier } from "react-admin";
import { z } from "zod";
import {
  applyFilter,
  applyPagination,
  applySort,
  areSetsEqual,
  assertNever,
  splitCompositeId,
  zodDateTime,
} from "../backoffice.utils";
import { doc, getDoc } from "firebase/firestore";
import { firestore } from "../firebase";
import { AbstractDataProvider } from "./AbstractDataProvider";
import { DateTime } from "luxon";
import type { TheoryQuestion } from "../model/TheoryQuestion";
import { parseFreeTextAnswer } from "../utils/parseFreeTextAnswer";
import { getTheoryQuestionsCatalog } from "../model/TheoryQuestionsCatalog";

export interface GivenAnswer {
  /** Indexes into `MultipleChoiceTheoryQuestion|ImageChoiceTheoryQuestion.options`. */
  selectedOptions?: number[];
  /** The order in which the options were presented to the student. */
  shuffledOptions?: number[];
  /** The free text answer(s) provided by the student. */
  enteredAnswers?: string[];
  /** Whether the student answered the question correctly. */
  hasBeenAnsweredCorrectly: boolean;
}

export interface TheoryExamSimulationResult {
  /** Composite id in the format `<studentId>:<simulationId>`. */
  id: string;
  errorPoints: number;
  passed: boolean;
  cancelled: boolean;
  timestamp: DateTime;
  /** Only populated when fetching a single record via `getOne`. */
  simulation?: Array<{ sectionTitle: string; questions: Array<TheoryQuestion> }>;
  /** Only populated when fetching a single record via `getOne`. */
  givenAnswers?: {
    [questionId: string]: GivenAnswer;
  };
}

class TheoryExamSimulationResultsProvider extends AbstractDataProvider<TheoryExamSimulationResult> {
  async getOne(
    _resource: string,
    { id }: GetOneParams<TheoryExamSimulationResult>,
  ): Promise<GetOneResult<TheoryExamSimulationResult>> {
    const [studentId, simulationId] = splitCompositeId(id);
    const record = (await _fetchTheoryExamSimulationResults(studentId)).find((r) => r.id === id);
    if (!record) {
      throw new Error(`TheoryExamSimulationResult ${id} not found`);
    }
    const { questionStates, simulation } = await _fetchTheoryExamSimulationResult(studentId, simulationId);
    const { version } = simulation.catalog;
    const catalog = await getTheoryQuestionsCatalog(version);
    record.simulation = simulation.sections.map((section) => ({
      sectionTitle: section.title,
      questions: section.questions.map((question) => catalog.questionsById[question.id]),
    }));
    record.givenAnswers = Object.fromEntries(
      Object.entries(questionStates).map(([questionId, questionState]) => [
        questionId,
        {
          selectedOptions: questionState.selectedOptions,
          shuffledOptions: questionState.shuffledOptions,
          enteredAnswers: questionState.enteredAnswers,
          hasBeenAnsweredCorrectly: _hasBeenAnsweredCorrectly(
            catalog.questionsById[questionId],
            questionState.selectedOptions,
            questionState.enteredAnswers,
          ),
        },
      ]),
    );
    return { data: record };
  }

  async getManyReference(
    _resource: string,
    { target, id, filter, sort, pagination }: GetManyReferenceParams,
  ): Promise<GetManyReferenceResult<TheoryExamSimulationResult>> {
    if (target !== "studentId") {
      throw new Error(`Unexpected target: ${JSON.stringify(target)} -- expected: "studentId"`);
    }
    const records = await _fetchTheoryExamSimulationResults(id);
    return applyPagination(applySort(applyFilter(records, filter), sort), pagination);
  }
}

export const theoryExamSimulationResultsProvider = new TheoryExamSimulationResultsProvider();

/** Used to parse a users/<studentId>/theory_learning/results Firestore document. */
const _ResultsDocSchema = z.object({
  results: z.array(
    z.object({
      errorPoints: z.number().int().nonnegative(),
      passed: z.boolean(),
      cancelled: z.boolean().optional(),
      timestamp: zodDateTime(),
    }),
  ),
});

/** Used to parse a users/<studentId>/theory_learning/simulation_<dateTime> Firestore document. */
const _SimulationDocSchema = z.object({
  questionStates: z.record(
    z.string(),
    z.object({
      selectedOptions: z.array(z.number().int().nonnegative()).optional(),
      shuffledOptions: z.array(z.number().int().nonnegative()).optional(),
      enteredAnswers: z.array(z.string()).optional(),
    }),
  ),
  simulation: z.object({
    catalog: z.object({
      version: z.string(),
      language: z.string(),
    }),
    sections: z.array(
      z.object({
        title: z.string(),
        questions: z.array(z.object({ id: z.string() })),
      }),
    ),
  }),
});

function _simulationIdFromTimestamp(timestamp: DateTime): string {
  return `simulation_${timestamp.setZone("Europe/Berlin").set({ millisecond: 0 }).toISO({ suppressMilliseconds: true, includeOffset: false })}`;
}

async function _fetchTheoryExamSimulationResults(studentId: Identifier): Promise<TheoryExamSimulationResult[]> {
  const data = (await getDoc(doc(firestore, `users/${studentId}/theory_learning/results`))).data();
  const records = (data ? _ResultsDocSchema.parse(data).results : []).map((result) => ({
    id: `${studentId}:${_simulationIdFromTimestamp(result.timestamp)}`,
    ...result,
    cancelled: result.cancelled ?? false,
  }));
  return records;
}

async function _fetchTheoryExamSimulationResult(
  studentId: Identifier,
  simulationId: string,
): Promise<z.infer<typeof _SimulationDocSchema>> {
  const simulationResultDoc = doc(firestore, `users/${studentId}/theory_learning/${simulationId}`);
  const data = (await getDoc(simulationResultDoc)).data();
  if (!data) {
    throw new Error(`Firestore document ${simulationResultDoc.path} not found`);
  }
  return _SimulationDocSchema.parse(data);
}

function _hasBeenAnsweredCorrectly(
  question: TheoryQuestion,
  selectedOptions: number[] | undefined,
  enteredAnswers: string[] | undefined,
): boolean {
  switch (question.typ) {
    case "Multiple-Choice-Frage":
    case "Bildauswahlfrage": {
      if (!selectedOptions) {
        throw new Error(`Selected options are undefined for question ${question.id}`);
      }
      const correctOptionsIndices = new Set<number>();
      for (let i = 0; i < question.options.length; i++) {
        if (question.options[i].isCorrect) {
          correctOptionsIndices.add(i);
        }
      }
      return areSetsEqual(new Set(selectedOptions), correctOptionsIndices);
    }
    case "Zahlenfrage":
      {
        if (!enteredAnswers) {
          throw new Error(`Entered answers are undefined for free text question ${question.id}`);
        }
        if (enteredAnswers.length !== question.correctAnswers.length) {
          throw new Error(
            `Number of entered answers (${enteredAnswers.length}) does not match number of correct answers (${question.correctAnswers.length}) for free text question ${question.id}`,
          );
        }
        const correctAnswers = question.correctAnswers;
        for (let i = 0; i < correctAnswers.length; i++) {
          const correctAnswer = correctAnswers[i];
          const enteredAnswer = enteredAnswers[i];
          if (parseFreeTextAnswer(enteredAnswer) !== correctAnswer) {
            return false;
          }
        }
      }
      return true;
    default:
      assertNever(question);
  }
}
